import { useState } from 'react';
import { useAuthStore } from '../../auth/AuthBoundary';
import { currentIdToken } from '../../auth/firebase';
import { ApiError } from '../../api/http';
import { plainFailure } from '../../api/plain-failure';
import { useResend } from './use-resend';

/** True when the sign-in service refused a second step because the account's address was never confirmed. */
export function addressUnconfirmed(e: unknown): boolean {
  return typeof e === 'object' && e !== null && 'code' in e && e.code === 'auth/unverified-email';
}

const trouble = 'That did not go through. Try again in a moment.';

/** Nobody is signed in to the sign-in service any more: the address to confirm is unknown. */
class SignedOut extends Error {}

/** Why no code was mailed: Paige's own sentence when it refused (too many wrong codes, a wait). */
function whyNotMailed(e: unknown): string {
  if (e instanceof SignedOut) return 'Sign out and sign in again to set up two-step sign-in.';
  return e instanceof ApiError && e.status !== 400 ? plainFailure(e, trouble) : trouble;
}

/**
 * Confirms the signed-in account's address with a code Paige mails to it, so an authenticator app
 * can be set up. A password account's address is confirmed by nobody, and the sign-in service
 * refuses a second step until it is: before this the person read "Verify your email address" with
 * no way to do it, and a company that required two-step sign-in locked them out (hardening,
 * 2026-10-06).
 */
export function useEmailConfirm(onConfirmed: () => void) {
  const store = useAuthStore();
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Mails a code to the signed-in account's address. */
  const mail = async (): Promise<void> => {
    const idToken = await currentIdToken();
    if (!idToken) throw new SignedOut();
    await store.sendEmailCode(idToken);
  };
  /** Mails the code and shows the step. */
  const ask = async (): Promise<void> => {
    setAsking(true);
    setError(null);
    await mail().catch((e: unknown) => setError(whyNotMailed(e)));
  };
  /** Send a new code: one request at a time, and the step says it went. */
  const resend = useResend(() => (setError(null), mail()), whyNotMailed);
  const confirm = async (code: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const idToken = await currentIdToken();
      if (!idToken) throw new Error('signed out');
      await store.confirmEmail(idToken, code);
      setAsking(false);
      onConfirmed();
    } catch (e) {
      setError(plainFailure(e, trouble));
    } finally {
      setBusy(false);
    }
  };
  return { asking, busy, error, ask, resend, confirm };
}
